import React from "react";
import { User, ChatMessage } from "../types";
import { XIcon, CheckIcon, CheckCheckIcon } from "./icons/FeatherIcons";
import { useCachedImage } from "../hooks/useCachedImage";

interface ReadReceiptsModalProps {
  message: ChatMessage;
  participants: User[];
  currentUserId: string;
  onClose: () => void;
}

// Helper component for cached participant avatar
const ParticipantAvatar: React.FC<{ participant: User }> = ({
  participant,
}) => {
  const cachedImage = useCachedImage(
    participant.profileImageUrl || "",
    participant.username,
    participant.id,
  );

  return participant.profileImageUrl ? (
    <img
      src={cachedImage}
      alt={participant.username}
      className="w-10 h-10 rounded-full object-cover"
    />
  ) : (
    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
      <span className="text-sm font-medium text-primary">
        {participant.username[0].toUpperCase()}
      </span>
    </div>
  );
};

const ReadReceiptsModal: React.FC<ReadReceiptsModalProps> = ({
  message,
  participants,
  currentUserId,
  onClose,
}) => {
  const readBySet = new Set(message.readBy || []);
  const deliveredToSet = new Set(message.deliveredTo || []);

  // Separate participants into categories
  const readParticipants = participants.filter(
    (p) => p.id !== currentUserId && readBySet.has(p.id),
  );
  const deliveredParticipants = participants.filter(
    (p) =>
      p.id !== currentUserId &&
      !readBySet.has(p.id) &&
      deliveredToSet.has(p.id),
  );
  const sentParticipants = participants.filter(
    (p) =>
      p.id !== currentUserId &&
      !readBySet.has(p.id) &&
      !deliveredToSet.has(p.id),
  );

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      tabIndex={-1}
      onPointerDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onTouchStart={(e) => {
        //e.preventDefault();
        e.stopPropagation();
      }}
      onTouchEnd={(e) => {
        //e.preventDefault();
        e.stopPropagation();
      }}
      onTouchCancel={(e) => {
        //e.preventDefault();
        e.stopPropagation();
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] flex flex-col"
        tabIndex={-1}
        onPointerDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onTouchStart={(e) => {
          //e.preventDefault();
          e.stopPropagation();
        }}
        onTouchEnd={(e) => {
          //e.preventDefault();
          e.stopPropagation();
        }}
        onTouchCancel={(e) => {
          //e.preventDefault();
          e.stopPropagation();
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Message Info</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <XIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Read section */}
          {readParticipants.length > 0 && (
            <div className="border-b border-gray-100">
              <div className="px-4 py-3 bg-gray-50">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <CheckCheckIcon className="w-4 h-4 text-blue-500" />
                  <span className="font-medium">Read</span>
                </div>
              </div>
              <div className="divide-y divide-gray-100">
                {readParticipants.map((participant) => (
                  <div
                    key={participant.id}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50"
                  >
                    <ParticipantAvatar participant={participant} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {participant.username}
                      </p>
                    </div>
                    <CheckCheckIcon className="w-4 h-4 text-blue-500 flex-shrink-0" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Delivered section */}
          {/* {deliveredParticipants.length > 0 && (
            <div className="border-b border-gray-100">
              <div className="px-4 py-3 bg-gray-50">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <CheckCheckIcon className="w-4 h-4 text-gray-400" />
                  <span className="font-medium">Delivered</span>
                </div>
              </div>
              <div className="divide-y divide-gray-100">
                {deliveredParticipants.map(participant => (
                  <div key={participant.id} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50">
                    <ParticipantAvatar participant={participant} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {participant.username}
                      </p>
                      {participant.name && (
                        <p className="text-xs text-gray-500 truncate">
                          {participant.name}
                        </p>
                      )}
                    </div>
                    <CheckCheckIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  </div>
                ))}
              </div>
            </div>
          )} */}

          {/* Sent section */}
          {sentParticipants.length > 0 && (
            <div>
              <div className="px-4 py-3 bg-gray-50">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <CheckIcon className="w-4 h-4 text-gray-400" />
                  <span className="font-medium">Sent</span>
                </div>
              </div>
              <div className="divide-y divide-gray-100">
                {sentParticipants.map((participant) => (
                  <div
                    key={participant.id}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50"
                  >
                    <ParticipantAvatar participant={participant} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {participant.username}
                      </p>
                    </div>
                    <CheckIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty state */}
          {/*readParticipants.length === 0 && deliveredParticipants.length === 0 && sentParticipants.length === 0 && ( */}
          {readParticipants.length === 0 && sentParticipants.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              <p className="text-sm">No delivery information available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReadReceiptsModal;
